/**
 * Per-browser preferences for the header notification bell (which live
 * categories from GET /api/v1/notifications are shown) plus the seen-ids
 * marker that drives the unread dot. Stored in localStorage; a missing or
 * blocked store falls back to defaults (everything on).
 */
import { useCallback, useEffect, useState } from 'react';

export type NotificationCategory = 'sla' | 'p1' | 'dispatch' | 'calls' | 'system';
export type NotificationPrefs = Record<NotificationCategory, boolean>;

export const NOTIFICATION_CATEGORIES: Array<{ key: NotificationCategory; label: string; description: string }> = [
  { key: 'p1', label: 'P1 Escalations', description: 'Open priority-1 tickets created in the last 24 hours' },
  { key: 'sla', label: 'SLA Breach Alerts', description: 'Open tickets breached or due within 4 hours' },
  { key: 'dispatch', label: 'Dispatch Recommendations', description: 'AI technician suggestions awaiting a decision' },
  { key: 'calls', label: 'Call Alerts', description: 'Unacknowledged high/critical alerts from call analysis' },
  { key: 'system', label: 'System Health', description: 'Integration errors and failed ConnectWise syncs (admins)' },
];

const PREFS_KEY = 'tsc-notification-prefs';
const SEEN_KEY = 'tsc-notifications-seen';
const EVENT = 'tsc-notification-prefs-change';

const DEFAULTS: NotificationPrefs = { sla: true, p1: true, dispatch: true, calls: true, system: true };

export function loadNotificationPrefs(): NotificationPrefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return { ...DEFAULTS };
    return { ...DEFAULTS, ...(JSON.parse(raw) as Partial<NotificationPrefs>) };
  } catch {
    return { ...DEFAULTS };
  }
}

export function useNotificationPrefs(): [NotificationPrefs, (key: NotificationCategory, value: boolean) => void] {
  const [prefs, setPrefs] = useState<NotificationPrefs>(loadNotificationPrefs);
  useEffect(() => {
    const sync = () => setPrefs(loadNotificationPrefs());
    window.addEventListener(EVENT, sync);
    return () => window.removeEventListener(EVENT, sync);
  }, []);
  const update = useCallback((key: NotificationCategory, value: boolean) => {
    const next = { ...loadNotificationPrefs(), [key]: value };
    try {
      localStorage.setItem(PREFS_KEY, JSON.stringify(next));
    } catch {
      // ignore: preference simply won't persist
    }
    setPrefs(next);
    window.dispatchEvent(new Event(EVENT));
  }, []);
  return [prefs, update];
}

/** Ids of notifications already seen in the bell (drives the unread dot). */
export function loadSeenIds(): Set<string> {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

export function saveSeenIds(ids: Iterable<string>): void {
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify([...ids].slice(-300)));
  } catch {
    // ignore
  }
}
